import { Download, Mail, Printer, X } from "lucide-react";
import { useRef, useState } from "react";
import { type CertificateFormat, warmCertificatePreview } from "../../../hooks/use-certificates";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { CertificateEmailPanel } from "./certificate-email-panel";
import { defaultFormatForCategory, formatOptions } from "./certificate-formats";

interface CertificateViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recordId: number;
  contactEmail: string | null;
  address: string | null;
  propertyCategory: string | null;
}

export function CertificateViewer({
  open,
  onOpenChange,
  recordId,
  contactEmail,
  address,
  propertyCategory,
}: CertificateViewerProps): React.ReactElement {
  const initialFormat = defaultFormatForCategory(propertyCategory);
  const [format, setFormat] = useState<CertificateFormat>(initialFormat);
  const [notesInput, setNotesInput] = useState("");
  const [appliedNotes, setAppliedNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const printFrameRef = useRef<HTMLIFrameElement>(null);
  const warmedFormatsRef = useRef<Set<CertificateFormat>>(new Set([initialFormat]));

  const buildUrl = (options: { download?: boolean } = {}): string => {
    const params = new URLSearchParams();
    if (options.download) params.set("download", "1");
    if (appliedNotes.trim()) params.set("notes", appliedNotes.trim());
    const qs = params.toString();
    return `/api/insurance/${recordId}/pdf/${format}${qs ? `?${qs}` : ""}`;
  };

  const viewUrl = buildUrl();

  const handleFormatChange = (next: CertificateFormat): void => {
    if (!warmedFormatsRef.current.has(next)) {
      warmedFormatsRef.current.add(next);
      warmCertificatePreview(recordId, next);
    }
    setFormat(next);
    setLoading(true);
    setLoadError(false);
  };

  const handleNotesBlur = (): void => {
    if (notesInput.trim() === appliedNotes.trim()) return;
    setAppliedNotes(notesInput);
    setLoading(true);
    setLoadError(false);
  };

  const handleDownload = (): void => {
    window.open(buildUrl({ download: true }), "_blank");
  };

  const handlePrint = (): void => {
    const frame = printFrameRef.current;
    if (!frame) return;
    frame.contentWindow?.print();
  };

  const handleClose = (nextOpen: boolean): void => {
    if (!nextOpen) {
      setShowEmail(false);
      setNotesInput("");
      setAppliedNotes("");
      setFormat(initialFormat);
      setLoading(true);
      setLoadError(false);
      warmedFormatsRef.current = new Set([initialFormat]);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex h-[100dvh] w-screen max-w-none flex-col gap-3 rounded-none border-0 p-4 sm:p-4">
        <DialogHeader className="flex-row items-center justify-between space-y-0 text-left">
          <DialogTitle>Certificate</DialogTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleClose(false)}
            aria-label="Close certificate viewer"
            data-testid="certificate-close-button"
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        {showEmail ? (
          <CertificateEmailPanel
            recordId={recordId}
            contactEmail={contactEmail}
            address={address}
            format={format}
            onSent={() => setShowEmail(false)}
            onCancel={() => setShowEmail(false)}
          />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-1 rounded-md border p-1" role="tablist" aria-label="Certificate format">
                {formatOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={format === opt.value}
                    onClick={() => handleFormatChange(opt.value)}
                    data-testid={`certificate-tab-${opt.value}`}
                    className={cn(
                      "rounded px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      format === opt.value
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="certificate-notes" className="whitespace-nowrap text-sm">
                  Notes
                </Label>
                <Input
                  id="certificate-notes"
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  onBlur={handleNotesBlur}
                  placeholder="Optional notes"
                  className="h-9 w-56"
                  data-testid="certificate-notes-input"
                />
              </div>
            </div>

            <div className="relative min-h-0 flex-1 overflow-hidden rounded-md border bg-muted">
              {loading && !loadError && (
                <div className="absolute inset-0 flex items-center justify-center" data-testid="certificate-loading">
                  <p className="text-sm text-muted-foreground">Loading certificate…</p>
                </div>
              )}
              {loadError ? (
                <div className="absolute inset-0 flex items-center justify-center" data-testid="certificate-error">
                  <p className="text-sm text-destructive">
                    The certificate could not be loaded. Try a different format or try again.
                  </p>
                </div>
              ) : (
                <iframe
                  key={viewUrl}
                  title="Certificate preview"
                  src={viewUrl}
                  className="h-full w-full"
                  data-testid="certificate-preview-frame"
                  onLoad={() => setLoading(false)}
                  onError={() => {
                    setLoading(false);
                    setLoadError(true);
                  }}
                />
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={handleDownload} data-testid="certificate-download-button">
                <Download className="h-3.5 w-3.5" />
                Download
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="certificate-print-button">
                <Printer className="h-3.5 w-3.5" />
                Print
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEmail(true)}
                data-testid="certificate-email-button"
              >
                <Mail className="h-3.5 w-3.5" />
                Email
              </Button>
            </div>

            <iframe ref={printFrameRef} title="Certificate print" src={viewUrl} className="hidden" aria-hidden="true" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
